import { useState } from 'react';

import { faAngleDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Check } from 'lucide-react';

import { cn } from '../lib/utils';

import { Button } from './button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from './dropdown-menu';

interface CheckboxDropdownProps<T> {
  darkMode?: boolean;
  disabled?: boolean;
  getItemLabel: (item: T) => string;
  getItemValue: (item: T) => string | null;
  items: T[];
  onChange: (items: T[]) => void;
  placeholder?: string;
  selectedItems: T[];
  buttonClassName?: string;
  noItemsFoundLabel?: string;
  type?: string;
  includeAllOption?: boolean;
}

const createAllOption = <T,>(items: T[]): T | null => {
  return (
    items.length > 0 && typeof items[0] === 'string' ? 'All' : null
  ) as T | null;
};

const handleAllOptionSelection = <T,>({
  items,
  selectedItems,
  getItemValue,
  onChange,
  includeAllOption = false,
}: {
  items: T[];
  selectedItems: T[];
  getItemValue: (item: T) => string | null;
  onChange: (items: T[]) => void;
  includeAllOption?: boolean;
}) => {
  if (!includeAllOption)
    return { itemsWithAll: items, isAllSelected: false, allOption: undefined };

  const allOption = createAllOption(items);
  const itemsWithAll = allOption ? [allOption, ...items] : items;

  // Check if "All" is selected
  const isAllSelected = selectedItems.length === 0;

  return { itemsWithAll, isAllSelected, allOption };
};

export const CheckboxDropdown = <T,>({
  darkMode = false,
  disabled,
  getItemLabel,
  getItemValue,
  items,
  onChange,
  placeholder = 'Select items',
  selectedItems,
  buttonClassName,
  noItemsFoundLabel = 'No items found',
  type = '',
  includeAllOption = false,
}: CheckboxDropdownProps<T>) => {
  const [isOpen, setIsOpen] = useState(false);

  const { itemsWithAll, isAllSelected, allOption } = handleAllOptionSelection({
    items,
    selectedItems,
    getItemValue,
    onChange,
    includeAllOption,
  });

  const handleChange = (item: T) => {
    const itemValue = getItemValue(item) ?? '';
    const isSelected = selectedItems.some(
      (selected) => (getItemValue(selected) ?? '') === itemValue,
    );

    // If clicking "All" option
    if (
      includeAllOption &&
      allOption &&
      itemValue === (getItemValue(allOption) ?? '')
    ) {
      // If "All" is already selected (selectedItems is empty), do nothing
      if (selectedItems.length === 0) {
        return;
      }
      onChange([]);
      return;
    }

    // If selecting any other item
    const newSelection = isSelected
      ? selectedItems.filter(
        (selected) => (getItemValue(selected) ?? '') !== itemValue,
      )
      : [...selectedItems, item];

    onChange(newSelection);
  };

  const selectedLabels = selectedItems.map(getItemLabel).join(', ');
  const newPlaceholder = isAllSelected
    ? `${placeholder}: All`
    : selectedItems.length < 4 && selectedItems.length > 0
      ? `${placeholder}: ${selectedLabels}`
      : selectedItems.length >= 4
        ? `${placeholder}: ${selectedItems.length} selected`
        : placeholder;

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild disabled={disabled}>
        <Button
          data-testid={`${type}-dropdown-trigger`}
          className={cn(
            'vai-justify-between vai-font-normal vai-text-left',
            'vai-transition-colors vai-duration-200',
            darkMode
              ? 'vai-bg-background-dark vai-border vai-border-sidebar-border vai-text-foreground-dark hover:vai-bg-zinc-800 hover:vai-border-zinc-600 hover:vai-text-foreground-dark'
              : 'vai-text-[#18181B]',
            buttonClassName,
          )}
          variant="outline"
        >
          <span className="vai-whitespace-nowrap vai-mr-2">
            {newPlaceholder}
          </span>
          <FontAwesomeIcon
            className={cn(
              'vai-flex-shrink-0',
              darkMode ? 'vai-text-gray-400' : 'vai-text-[#71717A]',
            )}
            icon={faAngleDown}
          />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent
        align="start"
        className={cn(
          'vai-w-[--radix-popper-anchor-width] vai-overflow-y-auto',
          darkMode &&
          '!vai-bg-background-muted vai-border vai-border-sidebar-border',
          buttonClassName === 'short'
            ? 'vai-max-h-[200px]'
            : 'vai-max-h-[65vh]',
        )}
      >
        {!itemsWithAll.length && (
          <DropdownMenuLabel
            className={cn({ 'vai-text-foreground-dark': darkMode })}
          >
            {noItemsFoundLabel}
          </DropdownMenuLabel>
        )}
        {itemsWithAll.map((item) => {
          const isSelected =
            isAllSelected && allOption
              ? (getItemValue(item) ?? '') === (getItemValue(allOption) ?? '')
              : selectedItems.some(
                (selected) =>
                  (getItemValue(selected) ?? '') ===
                  (getItemValue(item) ?? ''),
              );
          return (
            <Button
              className={cn(
                'vai-w-full !vai-justify-start !vai-px-2',
                darkMode
                  ? 'hover:!vai-bg-background-gray hover:!vai-text-foreground-dark !vai-bg-background-muted'
                  : 'hover:!vai-bg-slate-100  !vai-bg-inherit',
              )}
              key={getItemValue(item) as string}
              onClick={() => handleChange(item)}
              data-testid="dropdown-item"
            >
              <span className="vai-relative vai-w-[16px] vai-min-w-[16px] vai-h-[16px] vai-rounded-sm vai-items-center vai-justify-center vai-flex vai-p-0">
                <div
                  className={cn(
                    'vai-h-full vai-w-full vai-rounded-sm vai-border-2 vai-flex vai-items-center vai-justify-center',
                    isSelected
                      ? 'vai-bg-sapphire-600 vai-border-sapphire-600'
                      : 'vai-border-sapphire-600 vai-bg-transparent vai-border-[1px]',
                  )}
                />
                {isSelected && (
                  <Check className="vai-absolute vai-text-white vai-size-3" />
                )}
              </span>
              <p
                className={cn(
                  'vai-text-ellipsis vai-overflow-hidden vai-whitespace-nowrap',
                  darkMode
                    ? 'vai-text-foreground-dark hover:vai-text-foreground-dark'
                    : 'vai-text-[#18181B]',
                )}
              >
                {getItemLabel(item)}
              </p>
            </Button>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
